
  import { Component, Vue, Prop } from 'vue-property-decorator';
  import { mapGetters } from 'vuex';

  import { ILoginModel, ILoginPartnerResultModel, ISSODataModel } from '@/models/login.model';
  import { IPartnerLoginResultModel, IPartnerRegisterModel, IPartnerRegisterResultModel } from '@/models/register.model';

  import tokenService from '@/services/token.service';
  import errorsService from '@/services/errors.service';
  import accountService from '@/services/account.service';
  import cookieService from '@/services/cookie.service';

  enum LoginTypeEnum {
    Default,
    FirstSignup,
    RegisterConfirm
  }

  @Component({
    computed: {
      ...mapGetters([
        'redirectUrl',
        'clientId',
        'ssoData',
      ])
    }
  })
  export default class LoginPartnerCallback extends Vue {
    @Prop({ type: Object as () => ILoginModel })
    public user!: ILoginModel;

    data() {
      return {
        partnerLoginUrl: ''
      }
    };

    created() {
      this.$store.dispatch("updateIsLoading", true);

      if (this.$data.RCEnvironment.VUE_APP_ENVIRONMENT == 'FOCUS' || this.$data.RCEnvironment.VUE_APP_ENVIRONMENT == 'CREATIVE-ASSEMBLY') {
        const url = this.$data.RCEnvironment.VUE_APP_ACCOUNT_API_URL + (this.$data.RCEnvironment.VUE_APP_ENVIRONMENT == 'FOCUS' ? "focus" : "creativeassembly") + "/oauth/partner";

        this.$route.query.redirectUrl = this.$data.RCEnvironment.VUE_APP_ACCOUNT_URL + "oauth";

        accountService.get<ILoginPartnerResultModel>(this, url, { params: this.$route.query })
          .then(response => {
            const oauthRedirectUrl = localStorage.getItem("oauth_redirect_url");

            if (oauthRedirectUrl != null && oauthRedirectUrl != undefined) {
              this.$store.dispatch("updateRedirectUrl", oauthRedirectUrl);
              localStorage.removeItem("oauth_redirect_url");
            }

            if (response.CreateAccount) {
              return this.createAccount(response);
            }

            this.triggerEvent(LoginTypeEnum.Default);

            this.setG2GIdentityToken(response);
          })
          .catch(error => {
            this.$store.dispatch("updateIsLoading", false);

            errorsService.handleErrors(this, error);
          });
      } else if (this.$data.RCEnvironment.VUE_APP_ENVIRONMENT == 'STILLFRONT') {
        const url = this.$data.RCEnvironment.VUE_APP_ACCOUNT_API_URL + "stillfront/oauth/partner";

        this.$route.query.redirectUrl = this.$data.RCEnvironment.VUE_APP_ACCOUNT_URL + "oauth";
        const ssoData = this.$store.getters.ssoData;

        accountService.post<ISSODataModel, ILoginPartnerResultModel>(this, url, ssoData, { params: this.$route.query })
          .then(response => {
            if (response.CreateAccount) {
              return this.createAccount(response);
            }

            this.triggerEvent(LoginTypeEnum.Default);

            this.setG2GIdentityToken(response);
          })
          .catch(error => {
            this.$store.dispatch("updateIsLoading", false);

            errorsService.handleErrors(this, error);
          });
      } else {
        const url: string = this.$data.RCEnvironment.VUE_APP_ACCOUNT_API_URL + "partner/log-in";

        accountService.get<ILoginPartnerResultModel>(this, url, { params: this.$route.query })
          .then(response => {
            const now = new Date();
            this.$cookies.set("_g2g_identity_", response.Created, new Date(now.setDate(now.getDate() + 30)), undefined, undefined, undefined, "Strict");
            tokenService.setToken(this, true);
          })
          .catch(error => {
            this.$store.dispatch("updateIsLoading", false);
            errorsService.handleErrors(this, error);
          });
      }
    }

    public goToLoginView() {
      this.$emit('update:view', 'LoginHome');
    }

    private createAccount(response: ILoginPartnerResultModel): void {
      if (this.$data.RCEnvironment.VUE_APP_BYPASS_PARTNER_REGISTER_PAGE == true) {
        this.createOAuthAccount(response);
      } else {
        this.dispatchSSOInfos(response);
      }
    }

    private createOAuthAccount(loginResponse: ILoginPartnerResultModel): void {
      const url: string = this.$data.RCEnvironment.VUE_APP_ACCOUNT_API_URL + "partner/user/register";

      const form: IPartnerRegisterModel = {
        ApproveCodeOfConduct: true,
        ApproveEULA: true,
        ApprovePrivacyPolicy: true,
        ClientId: this.$store.getters.clientId,
        Cookies: this.$store.getters.cookies,
        Email: loginResponse.SSOEmail,
        Id_SSO: loginResponse.SSOId,
        Partner: this.$data.RCEnvironment.VUE_APP_PARTNER_NAME,
        SSORegisterCode: loginResponse.SSORegisterCode,
        Username: loginResponse.Username,
        SteamConnectUserInfo: loginResponse.SteamConnectUserInfo,
        EpicConnectUserInfo: loginResponse.EpicConnectUserInfo
      } as IPartnerRegisterModel;

      accountService.post<IPartnerRegisterModel, IPartnerRegisterResultModel>(this, url, form)
        .then(register => {
          this.triggerEvent(LoginTypeEnum.RegisterConfirm);

          const now = new Date();
          this.$cookies.set("_g2g_identity_", register.Created, new Date(now.setDate(now.getDate() + 30)), undefined, undefined, undefined, "Strict");

          this.triggerEvent(LoginTypeEnum.FirstSignup);

          this.$data.isLoginLoading = false;
          tokenService.setToken(this, true);
        })
        .catch(errors => {
          this.$data.errors = errors.data.ErrorMessages;
          this.$store.dispatch("updateIsLoading", false);
        });
    }

    private dispatchSSOInfos(response: ILoginPartnerResultModel): void {
      this.$store.dispatch("updateSSORegisterCode", response.SSORegisterCode);
      this.$store.dispatch("updateSSOId", response.SSOId);
      this.$store.dispatch("updateSSOEmail", response.SSOEmail);
      this.$store.dispatch("updateUsername", response.Username);
      this.$store.dispatch("updateIsLoading", false);

      this.$router.push({ name: 'PartnerRegister' });

      return;
    }

    private setG2GIdentityToken(response: ILoginPartnerResultModel): void {
      const now = new Date();
      this.$cookies.set("_g2g_identity_", response.Created, new Date(now.setDate(now.getDate() + 30)), undefined, undefined, undefined, "Strict");

      tokenService.setToken(this, true);
    }

    private triggerEvent(loginType: LoginTypeEnum = LoginTypeEnum.Default): void {
      const consentAnalytics = this.$cookies.get("consent_analytics");
      const isConsentAnalyticsAccepted = consentAnalytics != null && consentAnalytics == "1";

      if (!isConsentAnalyticsAccepted) {
        return;
      }

      switch (loginType) {
        case LoginTypeEnum.FirstSignup:
          cookieService.triggerFirstSignUpEvent(this);
          break;
        case LoginTypeEnum.RegisterConfirm:
          cookieService.triggerPartnerRegisterConfirmEvent(this);
          break;
        default:
          cookieService.triggerSuccessfullLoginEvent(this);
          break;
      }
    }
  }
